import React, { memo, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from '@agconnections/grow-ui';
import groupByCrop from 'screens/Property/helpers/groupByCrops';
import useMassAssignerTools from 'screens/Property/hooks/useMassAssignerTools';
import AddCropZonesModal from '../AddCropZonesModal';
import MassAssignerActions from '../MassAssignerActions';
import ExportPDFMapsFooter from '../../../PanelFooter/components/ExportPDFMapsFooter';
import GroupByCrop from './components/GroupByCrop';
import GroupByFarm from './components/GroupByFarms';
import './index.css';

const MassAssignerTree = ({
  properties,
  fieldsAndAreasGeoJSONCollection,
  propertiesFilter,
  fieldsToMassiveAssign,
  searchTextValueFFT,
  isExportPDFMapsActive,
  isSortByCrop,
  dispatch
}) => {
  const geoJSONMap = useMemo(() => {
    const map = new Map();
    fieldsAndAreasGeoJSONCollection?.features?.forEach(f => {
      map.set(f.properties.id, f);
    });
    return map;
  }, [fieldsAndAreasGeoJSONCollection]);

  const processedProperties = useMemo(() => {
    const mapped = properties.map(property => ({
      ...property,
      fields: property.fields.map(field => ({
        ...field,
        farmId: property.id,
        farmName: property.name
      }))
    }));
    return isSortByCrop
      ? groupByCrop(mapped)
      : propertiesFilter(mapped, searchTextValueFFT);
  }, [properties, isSortByCrop, propertiesFilter, searchTextValueFFT]);

  const {
    handleSelectedFarm,
    handleSelectedField,
    isFarmSelected,
    isFieldSelected,
    isCropZoneSelected,
    isPropertyIndeterminate,
    handleSelectedCropZone,
    handleSelectAllProperties,
    allPropertiesSelected,
    allPropertiesIndeterminate,
    isCropGroupSelected,
    handleSelectedCropGroup,
    handleSelectedFarmInCropGroup,
    handleSelectedFieldInCropGroup,
    isFarmSelectedInCropGroup,
    isFieldSelectedInCropGroup,
    isPropertyIndeterminateInCropGroup,
    isCropGroupIndeterminate
  } = useMassAssignerTools({
    processedProperties,
    fieldsToMassiveAssign,
    dispatch,
    isSortByCrop
  });
  const [openAddCropZonesModal, setOpenAddCropZonesModal] = useState(false);
  return (
    <div
      data-tree
      className="relative h-full flex-grow flex flex-col w-full overflow-y-auto"
      data-testid="mass-assigner-tree"
    >
      <div className="px-1 overflow-y-auto flex-grow flex-wrap overflow-x-hidden mass-assigner-tree-scroll">
        <Table>
          <Table.Row
            onClick={handleSelectAllProperties}
            selectable
            selected={allPropertiesSelected}
            indeterminate={allPropertiesIndeterminate()}
          >
            <Table.Cell>
              <div className="font-semibold text-sm">
                {searchTextValueFFT?.valueInput?.length > 2
                  ? 'Select all results'
                  : 'Select All Properties'}
              </div>
            </Table.Cell>
          </Table.Row>
          {isSortByCrop
            ? processedProperties.map(crop => (
                <GroupByCrop
                  key={crop.id}
                  crop={crop}
                  properties={properties}
                  handleSelectedCropGroup={handleSelectedCropGroup}
                  geoJSONMap={geoJSONMap}
                  isCropGroupSelected={isCropGroupSelected}
                  handleSelectedFarmInCropGroup={handleSelectedFarmInCropGroup}
                  isFarmSelectedInCropGroup={isFarmSelectedInCropGroup}
                  handleSelectedFieldInCropGroup={
                    handleSelectedFieldInCropGroup
                  }
                  isFieldSelectedInCropGroup={isFieldSelectedInCropGroup}
                  handleSelectedCropZone={handleSelectedCropZone}
                  isCropZoneSelected={isCropZoneSelected}
                  isPropertyIndeterminate={isPropertyIndeterminate}
                  isPropertyIndeterminateInCropGroup={
                    isPropertyIndeterminateInCropGroup
                  }
                  isCropGroupIndeterminate={isCropGroupIndeterminate}
                />
              ))
            : processedProperties.map(property => (
                <GroupByFarm
                  key={property.id}
                  property={property}
                  geoJSONMap={geoJSONMap}
                  handleSelectedFarm={handleSelectedFarm}
                  isFarmSelected={isFarmSelected}
                  handleSelectedField={handleSelectedField}
                  isFieldSelected={isFieldSelected}
                  handleSelectedCropZone={handleSelectedCropZone}
                  isCropZoneSelected={isCropZoneSelected}
                  isPropertyIndeterminate={isPropertyIndeterminate}
                />
              ))}
        </Table>
      </div>
      {isExportPDFMapsActive ? (
        <ExportPDFMapsFooter step={0} selectedFields={fieldsToMassiveAssign} />
      ) : (
        <MassAssignerActions
          dispatch={dispatch}
          fieldsToMassiveAssign={fieldsToMassiveAssign}
          setOpenAddCropZonesModal={setOpenAddCropZonesModal}
        />
      )}
      {openAddCropZonesModal && (
        <AddCropZonesModal
          open={openAddCropZonesModal}
          onClose={() => setOpenAddCropZonesModal(false)}
        />
      )}
    </div>
  );
};
MassAssignerTree.defaultProps = {
  properties: [],
  fieldsAndAreasGeoJSONCollection: {},
  fieldsToMassiveAssign: []
};
MassAssignerTree.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.object),
  fieldsAndAreasGeoJSONCollection: PropTypes.objectOf(PropTypes.any),
  propertiesFilter: PropTypes.func.isRequired,
  fieldsToMassiveAssign: PropTypes.arrayOf(PropTypes.object),
  searchTextValueFFT: PropTypes.objectOf(PropTypes.any).isRequired,
  isExportPDFMapsActive: PropTypes.bool.isRequired,
  isSortByCrop: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired
};
export default memo(MassAssignerTree);
